import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { API_send_welcome_message } from "../../../../../constants";
import { postRightworkAPI } from "../../utilities/requests";
import { formatName } from "../../../../../helpers/helpers";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 620,
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "calc(100vh - 64px)",
  bgcolor: "background.paper",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
  boxShadow: 24,
  overflow: "hidden",
};

const getEmployeeName = (employee) =>
  formatName(
    `${employee?.first_name || ""} ${employee?.last_name || ""}`.trim()
  );

export default function BatchWelcomeMessagesModal({
  open,
  onClose,
  employees = [],
  homeStore,
  onInvitesSent,
}) {
  const eligibleEmployees = React.useMemo(
    () => employees.filter((employee) => Boolean(employee?.email)),
    [employees]
  );
  const ineligibleCount = employees.length - eligibleEmployees.length;

  const [selectedIds, setSelectedIds] = React.useState([]);
  const [sendStatus, setSendStatus] = React.useState({});
  const [isSending, setIsSending] = React.useState(false);
  const [needsRefreshOnClose, setNeedsRefreshOnClose] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setSelectedIds(eligibleEmployees.map((employee) => employee.id));
      setSendStatus({});
      setIsSending(false);
      setNeedsRefreshOnClose(false);
    }
  }, [open, eligibleEmployees]);

  const selectedCount = selectedIds.length;
  const allSelected =
    eligibleEmployees.length > 0 && selectedCount === eligibleEmployees.length;
  const partiallySelected =
    selectedCount > 0 && selectedCount < eligibleEmployees.length;

  const toggleAll = () => {
    setSelectedIds(
      allSelected ? [] : eligibleEmployees.map((employee) => employee.id)
    );
  };

  const toggleEmployee = (employeeId) => {
    setSelectedIds((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const closeModal = () => {
    if (isSending) return;
    if (needsRefreshOnClose && typeof onInvitesSent === "function") {
      onInvitesSent();
    }
    onClose();
  };

  const sendInvites = async () => {
    if (isSending || selectedCount === 0) return;

    const selectedEmployees = eligibleEmployees.filter((employee) =>
      selectedIds.includes(employee.id)
    );
    let successCount = 0;
    let failureCount = 0;

    setIsSending(true);
    toast.loading(
      `Sending ${selectedEmployees.length} invite${selectedEmployees.length === 1 ? "" : "s"}...`,
      {
        autoClose: false,
      }
    );

    for (const employee of selectedEmployees) {
      setSendStatus((prev) => ({
        ...prev,
        [employee.id]: { state: "sending", message: "Sending" },
      }));

      try {
        const res = await postRightworkAPI(API_send_welcome_message, {
          store_id: homeStore,
          stm_id: employee.id,
          channel: "email",
        });

        if (res.status === 200) {
          successCount += 1;
          setSendStatus((prev) => ({
            ...prev,
            [employee.id]: { state: "sent", message: "Sent" },
          }));
        }
      } catch (error) {
        failureCount += 1;
        const message =
          error.response?.data?.system?.message || "Invite failed";
        setSendStatus((prev) => ({
          ...prev,
          [employee.id]: { state: "error", message },
        }));
      }
    }

    toast.dismiss();
    if (failureCount > 0) {
      toast.error(
        `${successCount} sent, ${failureCount} failed. Review the rows below.`,
        { autoClose: 4000 }
      );
    } else {
      toast.success(
        `${successCount} invite${successCount === 1 ? "" : "s"} sent`,
        { autoClose: 3000 }
      );
    }

    setIsSending(false);
    if (successCount > 0) {
      setNeedsRefreshOnClose(true);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        closeModal();
      }}
      aria-labelledby="batch-welcome-modal-title"
      aria-describedby="batch-welcome-modal-description"
    >
      <Box sx={modalStyle}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-700">
                <EmailIcon style={{ fontSize: 20 }} />
              </div>
              <h3
                id="batch-welcome-modal-title"
                className="text-lg font-semibold text-slate-900"
              >
                Send team invites
              </h3>
            </div>
            <p
              id="batch-welcome-modal-description"
              className="mt-2 text-sm text-slate-500"
            >
              Email welcome links to team members who have not logged in yet.
            </p>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={closeModal}
            disabled={isSending}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 py-4">
          {ineligibleCount > 0 && (
            <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {ineligibleCount} team member
              {ineligibleCount === 1 ? "" : "s"} need a real email before an
              invite can be sent.
            </div>
          )}

          <div className="rounded-lg border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
              <button
                type="button"
                className="flex items-center text-left text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={toggleAll}
                disabled={eligibleEmployees.length === 0 || isSending}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={partiallySelected}
                  onChange={toggleAll}
                  onClick={(event) => event.stopPropagation()}
                  disabled={eligibleEmployees.length === 0 || isSending}
                  sx={{
                    padding: "4px",
                    color: "#94a3b8",
                    "&.Mui-checked": { color: "#6d28d9" },
                    "&.MuiCheckbox-indeterminate": { color: "#6d28d9" },
                  }}
                />
                <span className="ml-2">
                  {selectedCount} of {eligibleEmployees.length} selected
                </span>
              </button>
              <span className="text-xs text-slate-500">Email invite</span>
            </div>

            <div className="max-h-[360px] overflow-y-auto">
              {eligibleEmployees.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500">
                  No team members have a usable email address for batch invites.
                </div>
              ) : (
                eligibleEmployees.map((employee) => {
                  const status = sendStatus[employee.id];
                  const isSelected = selectedIds.includes(employee.id);
                  return (
                    <button
                      key={employee.id}
                      type="button"
                      className="flex w-full items-center gap-3 border-b border-slate-100 px-3 py-3 text-left last:border-b-0 hover:bg-slate-50 disabled:cursor-not-allowed"
                      onClick={() => toggleEmployee(employee.id)}
                      disabled={isSending}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={isSending}
                        onChange={() => toggleEmployee(employee.id)}
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          padding: "4px",
                          color: "#94a3b8",
                          "&.Mui-checked": { color: "#6d28d9" },
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-slate-900">
                          {getEmployeeName(employee)}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {employee.email}
                        </div>
                      </div>
                      {status?.state === "sent" && (
                        <div className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                          <CheckCircleIcon style={{ fontSize: 16 }} />
                          Sent
                        </div>
                      )}
                      {status?.state === "error" && (
                        <div className="flex max-w-[180px] items-center gap-1 text-xs font-medium text-rose-600">
                          <ErrorOutlineIcon style={{ fontSize: 16 }} />
                          <span className="truncate">{status.message}</span>
                        </div>
                      )}
                      {status?.state === "sending" && (
                        <div className="text-xs font-medium text-slate-500">
                          Sending...
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <p className="text-xs text-slate-500">
            Sending again creates a fresh welcome link.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={closeModal}
              disabled={isSending}
            >
              Close
            </button>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:bg-violet-300"
              onClick={sendInvites}
              disabled={isSending || selectedCount === 0}
            >
              <SendIcon style={{ fontSize: 16 }} />
              {isSending
                ? "Sending..."
                : `Send ${selectedCount} invite${
                    selectedCount === 1 ? "" : "s"
                  }`}
            </button>
          </div>
        </div>
      </Box>
    </Modal>
  );
}
